import type { IComputedValue } from "mobx";
import { computed } from "mobx";
import type { HotkeyCommand } from "owa-hotkeys";
import { getCommands } from "owa-mail-hotkeys/lib/utils/MailModuleHotKeys";
import type { RibbonControlId } from "owa-ribbon-ids";
import { type MailRibbonControlId } from "owa-ribbon-ids/lib/mailRibbonId";
import { type ActionControlId } from "owa-ribbon-ids/lib/actionControlId";
import type { HoverTooltipOverrides } from "owa-command-ribbon";
import { createHoverTooltipFn } from "owa-command-ribbon";
import isWin32StylingEnabled from "owa-theme-win32-style-store/lib/selectors/isWin32StylingEnabled";
import { addToFavoritesDesc as Desc_addToFavoritesDesc, archiveButtonDesc as Desc_archiveButtonDesc, assignPolicyButtonDesc as Desc_assignPolicyButtonDesc, blockButtonDesc as Desc_blockButtonDesc, browseGroupsButtonDesc as Desc_browseGroupsButtonDesc, categorizeButtonDesc as Desc_categorizeButtonDesc, changeNoteColorDesc as Desc_changeNoteColorDesc, chatAroundEmail as Desc_chatAroundEmail, chatAroundEmailAll as Desc_chatAroundEmailAll, chatAroundEmailSplit as Desc_chatAroundEmailSplit, classicIndentedViewDesc as Desc_classicIndentedViewDesc, conversationsDesc as Desc_conversationsDesc, copilotReplyWithMeetingButtonDesc as Desc_copilotReplyWithMeetingButtonDesc, createQuickStepDesc as Desc_createQuickStepDesc, createRuleButtonDesc as Desc_createRuleButtonDesc, customizeButtonDesc as Desc_customizeButtonDesc, deleteButtonDesc as Desc_deleteButtonDesc, deleteNoteDesc as Desc_deleteNoteDesc, densityDesc as Desc_densityDesc, discardButtonDesc as Desc_discardButtonDesc, expandCollapseConversationDesc as Desc_expandCollapseConversationDesc, flagAddReminderButtonDesc as Desc_flagAddReminderButtonDesc, flagButtonDesc as Desc_flagButtonDesc, flagClearFlagButtonDesc as Desc_flagClearFlagButtonDesc, flagCustomButtonDesc as Desc_flagCustomButtonDesc, flagEditReminderButtonDesc as Desc_flagEditReminderButtonDesc, flagMarkCompleteButtonDesc as Desc_flagMarkCompleteButtonDesc, flagNextWeekButtonDesc as Desc_flagNextWeekButtonDesc, flagNoDateButtonDesc as Desc_flagNoDateButtonDesc, flagThisWeekButtonDesc as Desc_flagThisWeekButtonDesc, focusedInboxToggleDesc as Desc_focusedInboxToggleDesc, flagTodayButtonDesc as Desc_flagTodayButtonDesc, flagTomorrowButtonDesc as Desc_flagTomorrowButtonDesc, folderPaneDesc as Desc_folderPaneDesc, forwardButtonDesc as Desc_forwardButtonDesc, forwardEmailAsAttachmentDesc as Desc_forwardEmailAsAttachmentDesc, getAddinsButtonDesc as Desc_getAddinsButtonDesc, getM365AppsButtonDesc as Desc_getM365AppsButtonDesc, getMessageExtensionsButtonDesc as Desc_getMessageExtensionsButtonDesc, ignoreButtonDesc as Desc_ignoreButtonDesc, immersiveReaderButtonDesc as Desc_immersiveReaderButtonDesc, junkButtonDesc as Desc_junkButtonDesc, layoutDesc as Desc_layoutDesc, lightRPDesc as Desc_lightRPDesc, manageQuickStepsDesc as Desc_manageQuickStepsDesc, manageRulesButtonDesc as Desc_manageRulesButtonDesc, messagePreviewDesc as Desc_messagePreviewDesc, messageRecallDesc as Desc_messageRecallDesc, messagesDesc as Desc_messagesDesc, moreRetentionPoliciesButtonDesc as Desc_moreRetentionPoliciesButtonDesc, moveButtonDesc as Desc_moveButtonDesc, myDayButtonDesc as Desc_myDayButtonDesc, neverBlockButtonDesc as Desc_neverBlockButtonDesc, newFolderPostButtonDesc as Desc_newFolderPostButtonDesc, newItemsButtonDesc as Desc_newItemsButtonDesc, newMessageButtonDesc as Desc_newMessageButtonDesc, phishingButtonDesc as Desc_phishingButtonDesc, pinButtonDesc as Desc_pinButtonDesc, printEmailDesc as Desc_printEmailDesc, readUnreadButtonDesc as Desc_readUnreadButtonDesc, readingPaneDesc as Desc_readingPaneDesc, remindersWindowDesc as Desc_remindersWindowDesc, replyAllButtonDesc as Desc_replyAllButtonDesc, replyButtonDesc as Desc_replyButtonDesc, replyWithMeetingButtonDesc as Desc_replyWithMeetingButtonDesc, resendDesc as Desc_resendDesc, restoreAllDesc as Desc_restoreAllDesc, restoreDesc as Desc_restoreDesc, ribbonModeDesc as Desc_ribbonModeDesc, rulesButtonDesc as Desc_rulesButtonDesc, sendDraftButtonDesc as Desc_sendDraftButtonDesc, sensitivityButtonDesc as Desc_sensitivityButtonDesc, sensitivityLearnMoreButtonDesc as Desc_sensitivityLearnMoreButtonDesc, setupQuickStepsDesc as Desc_setupQuickStepsDesc, shareToTeamsChat as Desc_shareToTeamsChat, sendReceiveAllFoldersDesc as Desc_sendReceiveAllFoldersDesc, snoozeButtonDesc as Desc_snoozeButtonDesc, sweepButtonDesc as Desc_sweepButtonDesc, syncMailboxDesc as Desc_syncMailboxDesc, searchHighlightingDesc as Desc_searchHighlightingDesc, trackReadReceiptsDesc as Desc_trackReadReceiptsDesc, undoButtonDesc as Desc_undoButtonDesc, viewEmailFromNotesDesc as Desc_viewEmailFromNotesDesc, viewGroupsPanelButtonDesc as Desc_viewGroupsPanelButtonDesc, viewSettingsDesc as Desc_viewSettingsDesc, workOfflineDesc as Desc_workOfflineDesc, zoomDesc as Desc_zoomDesc, } from "./hoverTooltipDesc.locstring.json";
import type { ResourceId } from "owa-localize";

// Maps each control to its corresponding description string
// computed adds mobx reactivity to re-compute Map when observables change
const controlDescMap = computed(() => new Map<RibbonControlId, ResourceId>()
    .set(529, Desc_getAddinsButtonDesc)
    .set(7022, Desc_getM365AppsButtonDesc)
    .set(7016, Desc_getMessageExtensionsButtonDesc)
    .set(564, Desc_sensitivityButtonDesc)
    .set(565, Desc_sensitivityLearnMoreButtonDesc)
    .set(767, Desc_addToFavoritesDesc)
    .set(505, Desc_archiveButtonDesc)
    .set(616, Desc_assignPolicyButtonDesc)
    .set(507, Desc_blockButtonDesc)
    .set(658, Desc_blockButtonDesc)
    .set(508, Desc_browseGroupsButtonDesc)
    .set(509, Desc_categorizeButtonDesc)
    .set(644, Desc_conversationsDesc)
    .set(775, Desc_classicIndentedViewDesc)
    .set(696, Desc_copilotReplyWithMeetingButtonDesc)
    .set(698, Desc_copilotReplyWithMeetingButtonDesc)
    .set(516, Desc_createRuleButtonDesc)
    .set(755, Desc_createQuickStepDesc)
    .set(519, Desc_deleteButtonDesc)
    .set(649, Desc_densityDesc)
    .set(593, Desc_discardButtonDesc)
    .set(687, Desc_expandCollapseConversationDesc)
    .set(604, Desc_flagClearFlagButtonDesc)
    .set(704, Desc_flagCustomButtonDesc)
    .set(601, Desc_flagNextWeekButtonDesc)
    .set(602, Desc_flagNoDateButtonDesc)
    .set(600, Desc_flagThisWeekButtonDesc)
    .set(598, Desc_flagTodayButtonDesc)
    .set(599, Desc_flagTomorrowButtonDesc)
    .set(603, Desc_flagMarkCompleteButtonDesc)
    .set(527, Desc_flagButtonDesc)
    .set(732, Desc_flagAddReminderButtonDesc)
    .set(733, Desc_flagEditReminderButtonDesc)
    .set(774, Desc_focusedInboxToggleDesc)
    .set(647, Desc_folderPaneDesc)
    .set(531, Desc_ignoreButtonDesc)
    .set(532, Desc_immersiveReaderButtonDesc)
    .set(535, Desc_junkButtonDesc)
    .set(646, Desc_layoutDesc)
    .set(652, Desc_manageQuickStepsDesc)
    .set(537, Desc_manageRulesButtonDesc)
    .set(645, Desc_messagePreviewDesc)
    .set(686, Desc_messageRecallDesc)
    .set(654, Desc_messagesDesc)
    .set(539, Desc_moreRetentionPoliciesButtonDesc)
    .set(540, Desc_moveButtonDesc)
    .set(706, Desc_myDayButtonDesc)
    .set(659, Desc_neverBlockButtonDesc)
    .set(656, Desc_newFolderPostButtonDesc)
    .set(586, Desc_newItemsButtonDesc)
    .set(588, Desc_newMessageButtonDesc)
    .set(641, Desc_changeNoteColorDesc)
    .set(640, Desc_deleteNoteDesc)
    .set(642, Desc_viewEmailFromNotesDesc)
    .set(547, Desc_phishingButtonDesc)
    .set(548, Desc_pinButtonDesc)
    .set(549, Desc_printEmailDesc)
    .set(550, Desc_setupQuickStepsDesc)
    .set(552, Desc_readUnreadButtonDesc)
    .set(648, Desc_readingPaneDesc)
    .set(690, Desc_remindersWindowDesc)
    .set(553, Desc_replyWithMeetingButtonDesc)
    .set(657, Desc_phishingButtonDesc)
    .set(699, Desc_copilotReplyWithMeetingButtonDesc)
    .set(680, Desc_forwardButtonDesc)
    .set(679, Desc_replyButtonDesc)
    .set(681, Desc_replyAllButtonDesc)
    .set(700, Desc_replyWithMeetingButtonDesc)
    .set(555, Desc_forwardButtonDesc)
    .set(651, Desc_forwardEmailAsAttachmentDesc)
    .set(556, Desc_replyButtonDesc)
    .set(557, Desc_replyAllButtonDesc)
    .set(712, Desc_resendDesc)
    .set(683, Desc_restoreDesc)
    .set(684, Desc_restoreAllDesc)
    .set(558, Desc_customizeButtonDesc)
    .set(691, Desc_ribbonModeDesc)
    .set(559, Desc_rulesButtonDesc)
    .set(594, Desc_sendDraftButtonDesc)
    .set(571, Desc_snoozeButtonDesc)
    .set(572, Desc_sweepButtonDesc)
    .set(701, Desc_syncMailboxDesc)
    .set(562, Desc_sendReceiveAllFoldersDesc)
    .set(544, Desc_workOfflineDesc)
    .set(766, Desc_searchHighlightingDesc)
    .set(702, Desc_chatAroundEmail)
    .set(703, Desc_chatAroundEmailAll)
    .set(709, Desc_shareToTeamsChat)
    .set(697, Desc_trackReadReceiptsDesc)
    .set(580, Desc_undoButtonDesc)
    .set(663, Desc_viewGroupsPanelButtonDesc)
    .set(643, Desc_viewSettingsDesc)
    .set(688, Desc_zoomDesc)
    .set(720, Desc_lightRPDesc)
    .set(731, Desc_chatAroundEmailSplit));
// Maps each control to its corresponding hotkey command

/**
 * OWA/Monarch does not localize shortcut keys currently. However, there
 * are four (soon to be five) sets: Outlook.com, Google, Yahoo!, Outlook,
 * and win32. If/when those are supported, we'll have to have a more
 * complicated mapping.
 */

const controlHotkeyMap: IComputedValue<Map<RibbonControlId, HotkeyCommand>> = computed(() => {
    const commands = getCommands();
    
    // Added here because we don't want this to clash with the other zoom hotkey commands
    // And Ribbon is the only place where all the keys (zoom in, zoom out and reset zoom)
    // have to be displayed together for Zoom control tooltip in View tab
    const zoomHotKeyCommands: HotkeyCommand = {
        category: "emailActions",
        hotmail: ["ctrl++", "ctrl+-", "ctrl+0"],
        gmail: ["ctrl++", "ctrl+-", "ctrl+0"],
        owa: ["ctrl++", "ctrl+-", "ctrl+0"],
        outlook: ["ctrl++", "ctrl+-", "ctrl+0"],
    };
    
    const newItemsHotKeyCommand: HotkeyCommand = {
        category: "writeEmail",
        hotmail: "i",
        yahoo: "i",
        gmail: "i",
        owa: "i",
        outlook: "i",
    };
    
    /**
     * There IS a hotkey for toggling the flag (command.toggleFlag), but it swaps between
     * Flagged and Completed Flag, not Flagged and Unflagged.  We will leave it out of
     * the tooltip, as win32, so as not to cause confusion.
     */
    const controlMap = new Map<RibbonControlId, HotkeyCommand>()
        .set(505, commands.archiveMail)
        .set(509, commands.categorize)
        .set(519, commands.deleteMail)
        .set(593, commands.discardDraft)
        .set(531, commands.ignore)
        .set(535, commands.markAsJunk)
        .set(540, commands.moveToFolder)
        .set(587, commands.newMail)
        .set(588, commands.newMail)
        .set(552, commands.markAsReadUnread)
        .set(555, commands.forward)
        .set(680, commands.forward)
        .set(556, commands.reply)
        .set(679, commands.reply)
        .set(557, commands.replyAll)
        .set(681, commands.replyAll)
        .set(560, commands.saveDraft)
        .set(594, commands.sendMail)
        .set(624, commands.insertHyperlink)
        .set(571, commands.snooze)
        .set(580, commands.undoAction)
        .set(701, commands.syncMailbox)
        .set(562, commands.syncMailbox)
        .set(688, zoomHotKeyCommands);
    
    if (isWin32StylingEnabled()) {
        controlMap.set(586, newItemsHotKeyCommand);
    }
    
    return controlMap;
});

// Creates a function that returns the hover tooltip for a given combination of controlId and controlName
export const getHoverTooltip: (controlId: RibbonControlId | undefined, controlName: string, overrides?: HoverTooltipOverrides
/* eslint-disable-next-line owa-custom-rules/no-side-effects -- (https://aka.ms/OWALintWiki)
 * DO NOT COPY-PASTE!, this function can't be called in the module scope, please move it inside a function
 *	> Function createHoverTooltipFn called at module scope is not allowed. This may cause unwanted side effects. */
) => string | undefined = createHoverTooltipFn(controlDescMap, controlHotkeyMap);
